import React from "react";

import { css } from "@emotion/core";
import { ReactNode } from "react";
import { capitalize } from "lodash";

import {
  Radio,
  TagLabel,
  useTheme,
  Colors as ThemeColors,
  Shadows,
  Typography,
} from "@zapier/design-system";

import { Border, Colors } from "@zapier/checkout/esm/constants";

import { createKeyHandler } from "./createKeyHandler";

type Props = {
  enticement?: string;
  isCompact?: boolean;
  isDisabled?: boolean;
  isMetered?: boolean;
  isOnly?: boolean;
  isSelected?: boolean;
  name: string;
  onClick: () => void;
  price: ReactNode;
};

const billingOptionCss = (props: Props) =>
  css([
    css`
      align-items: center;
      border: ${Border.size.default} solid ${Border.color.default};
      border-radius: ${Border.radius.default};
      cursor: pointer;
      display: flex;
      flex: 1;
      flex-direction: row;
      margin: 0;
      padding: 15px;

      @media (max-width: 750px) {
        width: 100%;  }
      }
    `,
    props.isCompact &&
      css`
        flex: 5 1 0;

        &:not(:first-of-type) {
          flex: 3 1 0;
        }
      `,
    props.isDisabled &&
      css`
        cursor: default;
        opacity: 0.5;
      `,
    props.isOnly &&
      css`
        display: block;
        cursor: default;
        flex-basis: 100%;
        text-align: center;
      `,
    props.isSelected &&
      css`
        :not(:only-of-type) {
          border-color: ${Colors.heroine};
        }
      `,
  ]);

const descriptionCss = (props: Props) => css`
  text-align: ${props.isOnly ? "center" : "left"};
  margin-left: ${props.isOnly ? 0 : "20px"};
`;

const priceCss = css`
  font-size: 1.1em;
`;

const nameCss = css`
  color: ${Colors.deepSpace};
  font-size: 1.2em;
  font-weight: 600;
  margin-bottom: 10px;
  text-transform: capitalize;
`;

/* Rebrand styles below */
const rebrandBillingOptionCss = (props: Props) =>
  css([
    css`
      border: 1px solid ${ThemeColors.neutral400};
      cursor: pointer;
      border-radius: 3px;
      padding: 1.75em;
      flex: 1;

      @media (max-width: 769px) {
        width: 100%;
        }
      }
    `,
    props.isCompact &&
      css`
        flex: 5 1 0;

        &:not(:first-of-type) {
          flex: 3 1 0;
        }
      `,
    props.isDisabled &&
      css`
        cursor: default;
        opacity: 0.5;
      `,
    props.isOnly &&
      css`
        display: block;
        cursor: default;
      `,
    props.isSelected &&
      css`
        box-shadow: ${Shadows.elevation5};
      `,
  ]);

const intervalGrid = css`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
`;

const isSelectedCss = css`
  color: ${ThemeColors.blueJeans};
`;

const intervalDescription = (props: Props) => css`
  text-align: ${props.isOnly ? "center" : "left"};
  margin-left: ${props.isOnly ? 0 : "0.5em"};
`;

const intervalNameWrapper = css`
  color: ${ThemeColors.neutral800};
  ${Typography.paragraph3Semibold};
  display: inline-block;
`;

const intervalName = css`
  margin-right: 0.4em;
`;

const enticementCss = css`
  position: absolute;
  margin-top: -0.3em;
  text-transform: capitalize;
`;

const intervalPriceCss = (props: Props) => css`
  ${Typography.paragraph3};
  color: ${ThemeColors.neutral600};
  margin-left: ${props.isOnly ? 0 : "1.5em"};
`;

export const BillingOption = (props: Props) => {
  const { isRebrand } = useTheme();

  const {
    enticement,
    isDisabled = false,
    isMetered = false,
    isOnly = false,
    isSelected = false,
    name,
    onClick,
    price,
  } = props;

  if (isRebrand) {
    return (
      <div
        aria-checked={isSelected}
        aria-disabled={isDisabled}
        css={rebrandBillingOptionCss(props)}
        onClick={isDisabled ? () => {} : onClick}
        onKeyDown={isDisabled ? () => {} : createKeyHandler(["space"], onClick)}
        role="radio"
        tabIndex={0}
      >
        <div css={intervalGrid}>
          {!isOnly && (
            <Radio
              aria-label={name}
              checked={isSelected}
              name="billing-option"
              onChange={() => {}} // Handled by wrapper div
              value={String(price)}
            />
          )}
          <div css={intervalDescription(props)}>
            <div css={intervalNameWrapper}>
              <span css={[intervalName, props.isSelected && isSelectedCss]}>
                {capitalize(name)}
              </span>
              {enticement && (
                <span css={enticementCss}>
                  <TagLabel color="info">{enticement}</TagLabel>
                </span>
              )}
            </div>
          </div>
        </div>
        <div css={intervalPriceCss(props)}>
          {isMetered ? "Starts at " : ""}
          {price} per month
        </div>
      </div>
    );
  }

  return (
    <div
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      css={billingOptionCss(props)}
      onClick={isDisabled ? () => {} : onClick}
      onKeyDown={isDisabled ? () => {} : createKeyHandler(["space"], onClick)}
      role="radio"
      tabIndex={0}
    >
      {!isOnly && (
        <Radio
          aria-label={name}
          checked={isSelected}
          name="billing-option"
          onChange={() => {}} // Handled by wrapper div
          value={String(price)}
        />
      )}
      <div css={descriptionCss(props)}>
        <div css={nameCss}>
          {name} {enticement && <TagLabel color="info">{enticement}</TagLabel>}
        </div>

        <div css={priceCss}>
          {isMetered ? "Starts at " : ""}
          {price} per month
        </div>
      </div>
    </div>
  );
};
